import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Box, Loader, PageBlock, Stack } from 'braid-design-system';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import {
  CAREER_HISTORY,
  EDUCATION,
  LANGUAGES,
  LICENCES,
  PERSONAL_DETAILS,
  SKILLS,
  SUMMARY,
} from 'src/components/Router';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import { hotjarEvent } from 'src/modules/hotjar';

import { Loading } from '../Loading/Loading';

import translations from './.vocab';
import { CertsyFooter } from './CertsyFooter/CertsyFooter';
import { Header } from './Header/Header';
import { ReportProfile } from './ReportProfile/ReportProfile';
import { ShareableProfileEmptyPage } from './ShareableProfileEmptyPage/ShareableProfileEmptyPage';
import { ShareableProfileErrorPage } from './ShareableProfileErrorPage/ShareableProfileErrorPage';
import { useShareableProfile, useShareableProfilePage } from './data';
import { useShareableProfileViewLoaded } from './useShareableProfileViewLoaded';

import * as styles from './ShareableProfile.css';

export const ShareableProfile = () => {
  const { t } = useTranslations(translations);
  const { isLoading, isActive, isSelfView, error, showCertsyFooter } =
    useShareableProfilePage();
  const { brand } = useAppConfig();
  const { shareableProfile, shareableProfileCardEmpty } = useShareableProfile();
  const { personalDetails } = shareableProfile || {};

  const fullName = [personalDetails?.firstName, personalDetails?.lastName]
    .filter((value) => value)
    .join(' ');

  const mostRecentRole = shareableProfile?.roles[0];
  const jobTitle = mostRecentRole?.title.text || '';
  const company = mostRecentRole?.company.text || '';
  const isErrorOrInactive = error || isActive === false;

  const shareableProfileSlug = useTypedSelector(
    (store) => store.router.shareableProfileSlug,
  );

  const shareableProfileEmpty = Object.values(shareableProfileCardEmpty).every(
    (isEmpty) => isEmpty,
  );

  useShareableProfileViewLoaded({
    isLoading,
    isErrorOrInactive,
    isSelfView,
  });

  useEffect(() => {
    hotjarEvent('shareable-profile-viewer-survey');
  }, []);

  const showReportProfile = !isLoading && !isErrorOrInactive && !isSelfView;

  const shareableProfileUrl = useMelwaysLink({
    path: `/profile/${shareableProfileSlug}`,
    absolute: true,
  });

  return (
    <>
      <Helmet>
        <title>
          {!isErrorOrInactive &&
          !shareableProfileEmpty &&
          fullName &&
          jobTitle &&
          company
            ? t('Full Name - Job Title at Company | Brand', {
                fullName,
                jobTitle,
                company,
                brand,
              })
            : t('Profile | Brand', { brand })}
        </title>
        {fullName ? (
          <meta
            name="description"
            content={t(
              "See names's work experience, education, qualifications, skills...",
              { fullName, brand },
            )}
          />
        ) : null}
        <link rel="canonical" href={shareableProfileUrl} />
      </Helmet>
      <Box className={styles.pageContainer}>
        <PageBlock width="medium">
          <Stack space="xxxlarge">
            <Header />
            {isLoading ? (
              <Box display="flex" justifyContent="center">
                <Loader size="large" />
              </Box>
            ) : (
              <>
                {isErrorOrInactive ? (
                  <ShareableProfileErrorPage
                    error={Boolean(error)}
                    isActive={isActive}
                  />
                ) : (
                  <>
                    {!shareableProfileEmpty ? (
                      <Box>
                        <Loading card={PERSONAL_DETAILS} />
                        <Loading card={SUMMARY} />
                        <Loading card={CAREER_HISTORY} />
                        <Loading card={EDUCATION} />
                        <Loading card={LICENCES} />
                        <Loading card={SKILLS} />
                        <Loading card={LANGUAGES} />
                        {showCertsyFooter && <CertsyFooter />}
                      </Box>
                    ) : (
                      <ShareableProfileEmptyPage />
                    )}
                  </>
                )}
              </>
            )}
            {showReportProfile && <ReportProfile />}
          </Stack>
        </PageBlock>
      </Box>
    </>
  );
};
